import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * Adds the cart drawer's order bump. The bump markup is rendered with the drawer,
 * so after the add the drawer's own re-render (driven by CartLinesUpdateEvent)
 * replaces it with the next bump, or removes it when none is left.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} button - Add button.
 *
 * @extends Component<Refs>
 */
class CartOrderBump extends Component {
  requiredRefs = ['button'];

  /** @type {AbortController | null} */
  #abort = null;

  connectedCallback() {
    super.connectedCallback();
    this.#abort = new AbortController();
    this.refs.button.addEventListener('click', this.#add, { signal: this.#abort.signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    this.#abort = null;
  }

  #add = async () => {
    const { button } = this.refs;
    if (button.getAttribute('aria-busy') === 'true') return;

    const id = Number(this.dataset.variantId);
    const label = button.textContent;
    button.setAttribute('aria-busy', 'true');
    button.textContent = '…';

    const sections = [...document.querySelectorAll('cart-items-component')]
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
      .filter(Boolean)
      .join(',');

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: String(id), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchConfig('json', { body: JSON.stringify({ items: [{ id, quantity: 1 }], sections }) }),
      }).then((res) => res.json());
      if (response.status) throw new Error(response.description || 'Add to cart failed');

      const cart = await fetch(`${Theme.routes.cart_url}.js`).then((res) => res.json());
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, source: 'cart-order-bump', itemCount: 1, sections: response.sections, didError: false },
      });
    } catch (error) {
      deferred.reject(error);
      button.removeAttribute('aria-busy');
      button.textContent = label;
    }
  };
}

if (!customElements.get('cart-order-bump')) {
  customElements.define('cart-order-bump', CartOrderBump);
}
